import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { Colors, Icon } from "swing-components";

import { MarkdownContent } from "~components";
import type { AccordionAnswerType } from "~onboarding/utils";

type AccordionAnswerProps = Omit<AccordionAnswerType, "type"> & {
  onChange: (value: string) => void;
  /**
   * When `true`, does not call `onChange` when the component mounts.
   * @default: false
   */
  isReadOnly?: boolean;
};

/**
 * Component | AccordionAnswer
 *
 * A component that renders an accordion answer.
 */
export function AccordionAnswer(props: AccordionAnswerProps) {
  const { isReadOnly = false, onChange, value, accordions } = props;

  useEffect(() => {
    if (isReadOnly === false) {
      onChange(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {accordions.map((accordion) => (
        <Accordion label={accordion.label} markdown={accordion.markdown} key={accordion.label} />
      ))}
    </div>
  );
}

type AccordionProps = {
  label: string;
  markdown: string;
  isReadOnly?: boolean;
};

/**
 * Component | Accordion
 *
 * A component that renders an accordion.
 */
function Accordion(props: AccordionProps) {
  const { label, markdown } = props;

  /***** State *****/
  const [isOpen, setIsOpen] = useState(false);

  /***** Render *****/
  return (
    <button
      data-testid="accordion-toggle"
      onClick={() => setIsOpen(!isOpen)}
      css={{
        display: "flex",
        flexDirection: "column",
        transition: "gap 200ms",
        gap: !isOpen ? 0 : 16,
        boxShadow: "0px 2px 4px 0px #283c5d16",
        borderRadius: "8px",
        padding: 16,
        background: Colors.white300,
        color: Colors.black,
        textAlign: "left",
      }}
    >
      <header
        css={{
          width: "100%",
          fontSize: 16,
          fontWeight: 400,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
        }}
      >
        {label}
        <motion.div
          initial={{ rotate: 0 }}
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <Icon name="Chevron Down" />
        </motion.div>
      </header>

      {/* Divider */}
      <motion.hr
        css={{
          width: "100%",
          transformOrigin: "center",
          background: Colors.slate300,
          margin: 0,
          border: "none",
        }}
        initial={{ height: 0, opacity: 0, scaleX: 0 }}
        animate={{ height: isOpen ? 1 : 0, opacity: isOpen ? 1 : 0, scaleX: isOpen ? 1 : 0 }}
        transition={{ duration: 0.2 }}
      />

      {/* Content */}
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.2 }}
        css={{
          width: "100%",
          overflow: "hidden",
        }}
        aria-expanded={isOpen}
      >
        <MarkdownContent body={markdown} css={{ fontSize: 16 }} />
      </motion.div>
    </button>
  );
}
